import { useEffect } from 'react';

import { getEnv } from '@/shared/configs';
import { SplitFeatureFlag, useSplitFeatureFlag } from '../feature-flags';

const INFINITE_WATCH_SCRIPT_URL = 'https://app.infinitewatch.ai/watch.js';

const errorHandler = (event: ErrorEvent): void => {
  const msg = event.message ?? event.error?.message ?? '';
  if (msg.includes('https://ingest.infinitewatch.ai/v1/ingest')) {
    event.preventDefault();
  }
};

const rejectionHandler = (event: PromiseRejectionEvent): void => {
  const msg =
    typeof event.reason === 'string'
      ? event.reason
      : event.reason instanceof Error
        ? event.reason.message
        : '';
  if (msg.includes('https://ingest.infinitewatch.ai/v1/ingest')) {
    event.preventDefault();
  }
};

export function InfiniteWatchProvider() {
  const { isReady, isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.REN_10732_INFINITE_WATCH_SCRIPT,
  );
  const organizationId = getEnv().infinite_watch_organization_id;

  useEffect(() => {
    if (!isReady || !isFeatureFlagEnabled || !organizationId) {
      return;
    }

    window.addEventListener('error', errorHandler, true);
    window.addEventListener('unhandledrejection', rejectionHandler);

    const existingScript = document.querySelector(`script[src="${INFINITE_WATCH_SCRIPT_URL}"]`);
    if (existingScript) {
      return () => {
        window.removeEventListener('error', errorHandler, true);
        window.removeEventListener('unhandledrejection', rejectionHandler);
      };
    }

    const script = document.createElement('script');
    script.src = INFINITE_WATCH_SCRIPT_URL;
    script.setAttribute('data-iw-organization-id', organizationId);
    script.async = true;

    document.head.appendChild(script);

    return () => {
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', rejectionHandler);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [isReady, isFeatureFlagEnabled, organizationId]);

  return null;
}
